import { Box, Stack, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { Checkbox } from '@/components/form/baseInputs/Checkbox';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal/ConfirmationModal';
import { Callout } from '@/components/notifications/Callout/Callout';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { Loader } from '@/components/progress/Loader/Loader';
import { useReportError } from '@/hooks/useReportError';
import {
  getAddeparIntegrationLoginPath,
  getAddeparIntegrationLogoutPath,
} from '@/modules/assetProviderIntegrations/addepar/utils/addeparAuthUtils';
import { useCurrentUser } from '@/modules/authentication/hooks/useCurrentUser';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';
import { getPublicImageUrl } from '@/utils/staticFiles';

import { AddeparConfigurationSummary } from './AddeparConfigurationSummary';
import {
  AddeparIntegrationForm,
  isAddeparConfigured,
} from './AddeparIntegrationForm';
import {
  AdminIntegrationsPage_IntegrationConfigurationFragment,
  useSyncAddeparHouseholdSummaryMutation,
  useUpdateIntegrationConfigurationMutation,
} from './graphql/AdminIntegrationsPage.generated';
import { IntegrationUserSetupModal } from './IntegrationUserSetupModal';
import { ResyncTenantValuationsButton } from './ResyncTenantValuationsButton/ResyncTenantValuationsButton';

export interface AddeparIntegrationCardProps {
  // configuration will be null while loading
  configuration: AdminIntegrationsPage_IntegrationConfigurationFragment | null;
  refetch: () => void;
  isLoggedIn: boolean;
  externalUserName?: string | null;
  disabled?: boolean;
}

export function AddeparIntegrationCard({
  configuration,
  refetch,
  isLoggedIn,
  externalUserName,
  disabled = false,
}: AddeparIntegrationCardProps) {
  const [isEditingConfiguration, setIsEditingConfiguration] = useState(false);
  const addeparConfigured = isAddeparConfigured(configuration);

  if (!configuration) {
    return <Loader />;
  }

  return (
    <Stack spacing={1} position="relative">
      {isLoggedIn && (
        <Box position="absolute" right={-5} top={-5}>
          <ResyncTenantValuationsButton />
        </Box>
      )}
      <Box textAlign="center">
        <img
          alt="Addepar"
          src={getPublicImageUrl('/addeparLogo.svg')}
          height={30}
        />
      </Box>

      {addeparConfigured && !isEditingConfiguration ? (
        <AddeparIntegrationUserConnection
          onEditConnection={() => setIsEditingConfiguration(true)}
          configuration={configuration}
          isLoggedIn={isLoggedIn}
          externalUserName={externalUserName}
          refetch={refetch}
          disabled={disabled}
        />
      ) : (
        <AddeparIntegrationForm
          onEditComplete={() => {
            refetch();
            setIsEditingConfiguration(false);
          }}
          configuration={configuration}
          disabled={disabled}
        />
      )}
    </Stack>
  );
}

interface AddeparIntegrationUserConnectionProps {
  isLoggedIn: boolean;
  externalUserName?: string | null;
  configuration: AdminIntegrationsPage_IntegrationConfigurationFragment;
  onEditConnection: () => void;
  refetch: () => void;
  disabled?: boolean;
}

function AddeparIntegrationUserConnection({
  isLoggedIn,
  externalUserName,
  configuration,
  onEditConnection,
  refetch,
  disabled = false,
}: AddeparIntegrationUserConnectionProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEnableSyncModalOpen, setIsEnableSyncModalOpen] = useState(false);
  const currentPageURL = getCompletePathFromRouteKey('ADMIN_INTEGRATIONS', {});
  const currentUser = useCurrentUser();
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();

  const [updateConfiguration, { loading: isUpdatingConfiguration }] =
    useUpdateIntegrationConfigurationMutation({
      onCompleted: () => {
        showFeedback('Household summary sync enabled successfully', {
          variant: 'success',
        });
        refetch();
      },
      onError: (err) => {
        showFeedback(
          'Failed to enable household summary sync. Please refresh the page and try again.'
        );
        reportError('Failed to enable household summary sync', err);
      },
    });

  const [syncHouseholdSummary, { loading: isSyncing }] =
    useSyncAddeparHouseholdSummaryMutation({
      onCompleted: () => {
        showFeedback('Household summary sync started successfully', {
          variant: 'success',
        });
      },
      onError: (err) => {
        showFeedback(
          'Failed to sync household summary. Please refresh the page and try again.'
        );
        reportError('Failed to sync household summary', err);
      },
    });

  const queryParams = useMemo(
    () => ({
      is_integration_user: 'true',
      redirect: currentPageURL,
    }),
    [currentPageURL]
  );

  const getAddeparButtonProps = useCallback(() => {
    const isIntegrationUserLoggedInToAddepar = isLoggedIn;

    if (isIntegrationUserLoggedInToAddepar) {
      return {
        children: 'Disconnect integration user',
        variant: 'secondary' as const,
        href: getAddeparIntegrationLogoutPath(queryParams),
        method: 'post' as const,
      };
    } else {
      return {
        children: 'Connect integration user',
        variant: 'primary' as const,
        onClick: () => {
          setIsModalOpen(true);
        },
      };
    }
  }, [isLoggedIn, queryParams]);

  const handleEnableHouseholdSummarySync = useCallback(() => {
    setIsEnableSyncModalOpen(false);
    return updateConfiguration({
      variables: {
        input: {
          id: configuration.id,
          update: {
            addeparEnableHouseholdSummarySync: true,
          },
        },
      },
    });
  }, [configuration.id, updateConfiguration]);

  const handleSyncNow = useCallback(() => {
    return syncHouseholdSummary();
  }, [syncHouseholdSummary]);

  const isInternalUser = currentUser?.isInternalUser ?? false;
  const householdSummarySyncEnabled =
    configuration.addeparEnableHouseholdSummarySync;

  return (
    <Stack spacing={2}>
      <Typography variant="body-s">
        Manage the integration user for your Addepar account. This user should
        have access to all clients and portfolio data to enable regular syncing
        of information between systems.
        <br />
        <br />
        Please work with your Luminary team to configure this user correctly.
      </Typography>

      {/* you can only set up the integration user once addepar is configured */}
      <Button
        size="sm"
        target="_self"
        {...getAddeparButtonProps()}
        fullWidth
        external={isLoggedIn} // Only external if it's the disconnect button
        disabled={disabled}
      />

      {isLoggedIn && externalUserName && (
        <Callout severity="info-low">Linked using {externalUserName}</Callout>
      )}

      {householdSummarySyncEnabled && (
        <Button
          size="sm"
          variant="secondary"
          onClick={handleSyncNow}
          fullWidth
          disabled={disabled || isSyncing}
          loading={isSyncing}
        >
          Sync household summary attributes now
        </Button>
      )}

      {isInternalUser && !householdSummarySyncEnabled && (
        <Checkbox
          label="Enable household summary sync (internal)"
          value={false}
          disabled={disabled || isUpdatingConfiguration}
          onChange={(e) => {
            if (e.target.checked) {
              setIsEnableSyncModalOpen(true);
            }
          }}
        />
      )}

      <Box textAlign="left">
        <AddeparConfigurationSummary
          onEditConnection={onEditConnection}
          configuration={configuration}
          disabled={disabled}
        />
      </Box>

      <IntegrationUserSetupModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onContinue={() => {
          setIsModalOpen(false);
          window.location.href = getAddeparIntegrationLoginPath(queryParams);
        }}
        providerName="Addepar"
      />

      <ConfirmationModal
        isOpen={isEnableSyncModalOpen}
        heading="Enable household summary sync"
        confirmText="Enable"
        onClose={(reason) => {
          if (reason === 'confirm') {
            handleEnableHouseholdSummarySync();
          } else {
            setIsEnableSyncModalOpen(false);
          }
        }}
      >
        This enables a two-way sync with Addepar that will sync a number of
        attributes from Luminary back to each connected household on Addepar.
        Note that it requires additional permissions, so a customer
        administrator will need to disconnect and reconnect the integration user
        to enable the flow.
      </ConfirmationModal>
    </Stack>
  );
}
